import type { Language } from '@offer-catalog/services/i18n'
import type { CustomViewSelections, CustomViewTarget } from './customViewConfig'
import type { ProductsContextType, SortCriteria, ViewMode } from './ProductsContext'
import { getCurrentLanguage } from '@offer-catalog/services/i18n'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router'
import { useCountriesContext } from '@/contexts/countries/useCountriesContext'
import { useProductFacets, useProductsInfiniteQuery } from '@/hooks/products'
import { DEFAULT_LIST_SELECTIONS, DEFAULT_SELECTIONS, LIST_SELECTABLE_DATA_POINTS, loadSelectionsFromStorage, saveSelectionsToStorage } from './customViewConfig'
import { getEffectiveViewMode, getFacetFiltersForView, getVisibleFilterGroups } from './facetFilters'
import { mapFacetsToFilterGroups } from './facets'
import { DEFAULT_SORT_ORDERS, getProductOrder, GROUP_BY_SM_CRITERIA, MAX_COLUMNS, MIN_COLUMNS, SORT_CRITERIA } from './ProductsContext'
import { useFacetUrlSync } from './useFacetUrlSync'

export function useProducts(customViewTarget: CustomViewTarget = 'gallery'): ProductsContextType {
  const { t } = useTranslation()
  const params = useParams<{ season: string, universeId: string, departmentId: string, subdepartmentId?: string }>()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentLanguage = getCurrentLanguage() as Language
  const { countries } = useCountriesContext()

  const season = params.season as string
  const universeId = Number(params.universeId)
  const departmentId = Number(params.departmentId)
  const subdepartmentId = params.subdepartmentId ? Number(params.subdepartmentId) : undefined
  const selectedSubdepartmentIds = searchParams.getAll('subdepartments').map(Number).filter(Number.isFinite)
  let subdepartmentIds: number[] | undefined
  if (selectedSubdepartmentIds.length > 0) {
    subdepartmentIds = selectedSubdepartmentIds
  }
  else if (subdepartmentId !== undefined) {
    subdepartmentIds = [subdepartmentId]
  }
  const isFullDepartment = location.pathname.includes('/all-families')
  const familiesIds = searchParams.getAll('families').map(Number)
  const productCode = searchParams.get('product_code')?.trim() || undefined
  const urlSortCriteria = searchParams.get('sort') as SortCriteria | null
  const urlSortOrder = searchParams.get('order')
  const hasValidSort = urlSortCriteria && SORT_CRITERIA.includes(urlSortCriteria)
  const hasValidOrder = urlSortOrder === 'asc' || urlSortOrder === 'desc'
  const sortCriteria = hasValidSort ? urlSortCriteria : 'segmentation'
  const sortOrder = hasValidOrder ? urlSortOrder : DEFAULT_SORT_ORDERS[sortCriteria]
  const sortOrderChosen = urlSortOrder === 'asc' || urlSortOrder === 'desc'

  const order = getProductOrder(sortCriteria, sortOrder)

  const [viewMode, setViewMode] = useState<ViewMode>(() => searchParams.get('viewMode') === 'model' ? 'model' : 'sm')
  const requestedViewMode = searchParams.get('viewMode')
  const effectiveViewMode = getEffectiveViewMode(customViewTarget, requestedViewMode, viewMode)
  const isHomologationCountryFilterAvailable = customViewTarget === 'gallery' && effectiveViewMode === 'model'
  const [columns, setColumns] = useState(MIN_COLUMNS)
  const [isCustomViewOpen, setIsCustomViewOpen] = useState(false)
  const [isFiltersOpen, setIsFiltersOpen] = useState(!!productCode)

  const facetFilters = getFacetFiltersForView(searchParams, countries, isHomologationCountryFilterAvailable)
  const isNew = searchParams.has('is_new')
  const productSearchOptions = {
    season,
    familiesIds,
    subdepartmentIds,
    viewMode: effectiveViewMode,
    productCode,
    currentLanguage,
    filters: facetFilters,
    isNew,
  }

  const {
    data: filterGroups = mapFacetsToFilterGroups(t),
    facetQueryKey,
    isFetching: isFetchingFacets,
    isSuccess: isFacetQuerySuccessful,
    isError: isFacetQueryError,
    isPlaceholderData: isFacetDataPlaceholder,
    loadFacetGroupLabels,
    loadingFacetGroups,
  } = useProductFacets({
    ...productSearchOptions,
    departmentId: isFullDepartment ? departmentId : undefined,
  })

  const isFacetResponseReady = isFacetQuerySuccessful && !isFetchingFacets && !isFacetDataPlaceholder
  const visibleFilterGroups = getVisibleFilterGroups(filterGroups, isHomologationCountryFilterAvailable)
  const invalidFacetSelections = useFacetUrlSync({
    searchParams,
    setSearchParams,
    filterGroups: visibleFilterGroups,
    isHomologationCountryFilterAvailable,
    isFacetResponseReady,
  })
  const hasFacetScope = isFullDepartment || familiesIds.length > 0 || (subdepartmentIds?.length ?? 0) > 0
  const isProductsQueryEnabled = !hasFacetScope
    || isFacetQueryError
    || (isFacetResponseReady && invalidFacetSelections.length === 0)

  const {
    products,
    pages,
    sentinelRef,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    isLoading,
    isError,
  } = useProductsInfiniteQuery({
    ...productSearchOptions,
    universeId,
    departmentId,
    isFullDepartment,
    enabled: isProductsQueryEnabled,
    order,
  })

  const isProductsQueryLoading = isLoading || (!isProductsQueryEnabled && !isFacetQueryError)

  const [customViewSelections, setCustomViewSelections]
    = useState<CustomViewSelections>(
      () => {
        const defaults = customViewTarget === 'list' ? DEFAULT_LIST_SELECTIONS : DEFAULT_SELECTIONS
        const storedSelections = loadSelectionsFromStorage(customViewTarget)

        if (!storedSelections)
          return { ...defaults, selectedItems: [...defaults.selectedItems] }

        return customViewTarget === 'list'
          ? { selectedItems: storedSelections.selectedItems.filter(item => LIST_SELECTABLE_DATA_POINTS.includes(item)) }
          : storedSelections
      },
    )

  const zoomIn = () => setColumns(prev => Math.max(MIN_COLUMNS, prev - 1))
  const zoomOut = () => setColumns(prev => Math.min(MAX_COLUMNS, prev + 1))

  const onSortCriteria = (criteria: SortCriteria) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      next.set('sort', criteria)
      next.set('order', DEFAULT_SORT_ORDERS[criteria])
      return next
    })
  }

  const toggleSortOrder = () => {
    const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      next.set('sort', sortCriteria)
      next.set('order', nextOrder)
      return next
    })
  }

  const onCustomViewSelections = (selections: CustomViewSelections) => {
    setCustomViewSelections(selections)
    saveSelectionsToStorage(selections, customViewTarget)
  }

  const openCustomView = () => setIsCustomViewOpen(true)
  const closeCustomView = () => setIsCustomViewOpen(false)

  const groupBySM = GROUP_BY_SM_CRITERIA.includes(sortCriteria)

  return {
    closeCustomView,
    columns,
    customViewTarget,
    customViewSelections,
    filterGroups: visibleFilterGroups,
    facetQueryKey,
    isFetchingFacets,
    groupBySM,
    isCustomViewOpen,
    isFiltersOpen,
    openCustomView,
    onCustomViewSelections,
    setIsFiltersOpen,
    onSortCriteria,
    setViewMode,
    sortCriteria,
    sortOrder,
    sortOrderChosen,
    products,
    pages,
    sentinelRef,
    isFetchingNextPage,
    hasNextPage,
    isLoading: isProductsQueryLoading,
    isError,
    isRefreshing: isFetchingFacets || (isFetching && !isLoading),
    loadFacetGroupLabels,
    loadingFacetGroups,
    toggleSortOrder,
    viewMode: effectiveViewMode,
    zoomIn,
    zoomOut,
  }
}
